import { z } from 'zod';
import { palletBaseTypeSchema, IPalletBaseType } from './palletBaseType';

/**
 * Schema for validating pallet base type form data.
 * Extends the base pallet base type schema with form-specific validations.
 *
 * @property {string} code - Pallet base type code (max 5 characters)
 * @property {string} name - Pallet base type name (max 255 characters)
 * @property {number} defaultWeight - Default weight in kilograms (min 0)
 */
export const palletBaseTypeFormSchema = palletBaseTypeSchema.extend({});

/**
 * Default form schema for new pallet base types.
 * Makes all required fields optional with empty defaults.
 */
export const defaultPalletBaseTypeFormSchema = palletBaseTypeFormSchema.extend({
    code: z.string().max(5, 'Code can only be up to 5 characters long.').optional().default(''),
    name: z.string().max(255, 'Name can only be up to 255 characters long.').optional().default(''),
});

// Infer the TypeScript type from the Zod schema
export type IPalletBaseTypeFormValues = z.infer<typeof palletBaseTypeFormSchema>;

/**
 * Factory function to create form values for a pallet base type.
 * Handles both new and existing pallet base types.
 *
 * @param {IPalletBaseType} [palletBaseType] - Optional existing pallet base type to populate form
 * @returns {IPalletBaseTypeFormValues} Form values with appropriate defaults
 */
export const PalletBaseTypeFormValues = (palletBaseType ?: IPalletBaseType) : IPalletBaseTypeFormValues => {
    // Handle the default case (new pallet base type)
    if (!palletBaseType) {
        return defaultPalletBaseTypeFormSchema.parse({});
    }

    // Transform the IPalletBaseType data into IPalletBaseTypeFormValues structure
    return palletBaseTypeFormSchema.parse({
        ...palletBaseType,
    });
};

/**
 * Validates pallet base type form values.
 * Logs any validation errors to the console.
 *
 * @param {IPalletBaseTypeFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation passes, false otherwise
 */
export const validatePalletBaseTypeForm = (formValues : IPalletBaseTypeFormValues) : boolean => {
    try {
        palletBaseTypeFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Pallet base type form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
